import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Container, Row, Col, Form, Spinner, Modal } from "react-bootstrap";
import { FaMinus, FaPlus, FaLock, FaHeart, FaCalculator, FaEdit, FaInfoCircle, FaChevronDown, FaTruck, FaCheckCircle, FaLightbulb } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { createBookDonationSession } from "../../apis/bookDonationApi";
import "./BookSponsorshipPage.css";

const BookSponsorshipPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const schoolFromQuery = queryParams.get("school")?.toLowerCase();
  const initialSchool =
    schoolFromQuery === "tsu"
      ? "tsu"
      : schoolFromQuery === "pvamu"
        ? "pvamu"
        : "hbcu";
  const hasSchoolInQuery = Boolean(queryParams.get("school"));

  const [selectedSchool, setSelectedSchool] = useState(initialSchool);

  const schoolConfig = {
    tsu: {
      code: "TSU",
      shortName: "TSU",
      name: "Texas Southern University",
      fullName: "Texas Southern University (TSU)",
      editionBadge: "TSU Campus Edition",
      coverImage: "/TSU Love Smarter Book Mockup.webp",
      coverAlt: "Texas Southern University (TSU) Campus Edition",
      accentColor: "#6F1D2F",
      subtext: "Provide life-changing books directly to students at Texas Southern University.",
    },
    pvamu: {
      code: "PVAMU",
      shortName: "PVAMU",
      name: "Prairie View A&M University",
      fullName: "Prairie View A&M University (PVAMU)",
      editionBadge: "PVAMU Campus Edition",
      coverImage: "/PVAMU Love Smarter Box Cover.webp",
      coverAlt: "Prairie View A&M University (PVAMU) Campus Edition",
      accentColor: "#46166b",
      subtext: "Provide life-changing books directly to students at Prairie View A&M University.",
    },
    hbcu: {
      code: "HBCU",
      shortName: "HBCU",
      name: "HBCU Students",
      fullName: "General HBCU Population",
      editionBadge: "HBCU Edition",
      coverImage: "/hbcu_love.webp",
      coverAlt: "HBCU Hope Project Edition",
      accentColor: "#d97706",
      subtext: "Provide life-changing books to help students build stronger relationships.",
    },
  };

  const currentSchool = schoolConfig[selectedSchool] || schoolConfig.hbcu;

  // Redux User State
  const { user } = useSelector((state) => state.user || {});

  // Section A: Sponsor Books for Students
  const [addStudentSponsorship, setAddStudentSponsorship] = useState(hasSchoolInQuery || false);
  const [sponsoredQty, setSponsoredQty] = useState(2);

  const initialOrgOption =
    schoolFromQuery === "tsu"
      ? "tsu"
      : schoolFromQuery === "pvamu"
        ? "pvamu"
        : "hbcu";

  const initialOrgName =
    schoolFromQuery === "tsu"
      ? "Texas Southern University (TSU)"
      : schoolFromQuery === "pvamu"
        ? "Prairie View A&M University (PVAMU)"
        : "HBCU Hope Project";

  const [selectedOrgOption, setSelectedOrgOption] = useState(initialOrgOption);
  const [organizationName, setOrganizationName] = useState(initialOrgName);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);

  const handleSelectOrg = (optionKey) => {
    setSelectedOrgOption(optionKey);
    setOrgDropdownOpen(false);
    if (optionKey === "tsu") {
      setSelectedSchool("tsu");
      setOrganizationName("Texas Southern University (TSU)");
    } else if (optionKey === "pvamu") {
      setSelectedSchool("pvamu");
      setOrganizationName("Prairie View A&M University (PVAMU)");
    } else if (optionKey === "hbcu") {
      setSelectedSchool("hbcu");
      setOrganizationName("HBCU Hope Project");
    }
  };

  // Section B: Purchaser Info
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Section B: Shipping Address
  const [shippingStreet, setShippingStreet] = useState("");
  const [shippingApt, setShippingApt] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef(null);

  // Delivery Address options for Church & Personalized
  const [churchDelivery, setChurchDelivery] = useState("same");
  const [churchDelStreet, setChurchDelStreet] = useState("");
  const [churchDelApt, setChurchDelApt] = useState("");
  const [churchDelCity, setChurchDelCity] = useState("");
  const [churchDelState, setChurchDelState] = useState("");
  const [churchDelZip, setChurchDelZip] = useState("");
  const [churchStateDropdownOpen, setChurchStateDropdownOpen] = useState(false);
  const churchStateDropdownRef = useRef(null);

  const [personalizedDelivery, setPersonalizedDelivery] = useState("same");
  const [personalizedDelStreet, setPersonalizedDelStreet] = useState("");
  const [personalizedDelApt, setPersonalizedDelApt] = useState("");
  const [personalizedDelCity, setPersonalizedDelCity] = useState("");
  const [personalizedDelState, setPersonalizedDelState] = useState("");
  const [personalizedDelZip, setPersonalizedDelZip] = useState("");
  const [personalizedStateDropdownOpen, setPersonalizedStateDropdownOpen] = useState(false);
  const personalizedStateDropdownRef = useRef(null);

  // Section C: Personal Copies (Optional)
  const [addPersonalizedCopies, setAddPersonalizedCopies] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [personalizedMessage, setPersonalizedMessage] = useState("");
  const [personalizedQty, setPersonalizedQty] = useState(1);
  const personalizedPrice = 39.97;

  // Section D: Add Copies for Your Church (Optional)
  const [addChurchCopies, setAddChurchCopies] = useState(false);
  const [churchQty, setChurchQty] = useState(15);
  const [ministryType, setMinistryType] = useState("");
  const [churchName, setChurchName] = useState("");
  const churchPrice = 20.0;
  const [ministryDropdownOpen, setMinistryDropdownOpen] = useState(false);
  const ministryRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ministryRef.current && !ministryRef.current.contains(event.target)) {
        setMinistryDropdownOpen(false);
      }
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setOrgDropdownOpen(false);
      }
    };
    const handleStateClickOutside = (event) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setStateDropdownOpen(false);
      }
      if (churchStateDropdownRef.current && !churchStateDropdownRef.current.contains(event.target)) {
        setChurchStateDropdownOpen(false);
      }
      if (personalizedStateDropdownRef.current && !personalizedStateDropdownRef.current.contains(event.target)) {
        setPersonalizedStateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("mousedown", handleStateClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("mousedown", handleStateClickOutside);
    };
  }, []);

  // Redirect to existing success or cancel page if returned with query status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("success") === "true" ||
      params.get("payment") === "success" ||
      params.get("status") === "success"
    ) {
      const search = new URLSearchParams(window.location.search);
      if (!search.get("type")) {
        search.set("type", "book-donation");
      }
      navigate(`/success?${search.toString()}`, { replace: true });
    } else if (
      params.get("cancel") === "true" ||
      params.get("canceled") === "true" ||
      params.get("cancelled") === "true"
    ) {
      navigate("/cancel", { replace: true });
    }
  }, [navigate]);

  // Synchronize school query parameter if url changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const school = params.get("school")?.toLowerCase();
    if (school === "pvamu") {
      setSelectedSchool("pvamu");
      setAddStudentSponsorship(true);
    } else if (school === "tsu") {
      setSelectedSchool("tsu");
      setAddStudentSponsorship(true);
    }
  }, [location.search]);

  // Section E: Wall of Promise Message (Optional)
  const [wallOfPromiseMessage, setWallOfPromiseMessage] = useState("");
  const [requestVideoSupport, setRequestVideoSupport] = useState(false);

  // Section F: Additional Donation (Optional)
  const [addAdditionalDonation, setAddAdditionalDonation] = useState(false);
  const [donationAmount, setDonationAmount] = useState("");

  // Order Summary & Form State
  const [receiveUpdates, setReceiveUpdates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCalcModal, setShowCalcModal] = useState(false);

  // Temporary state for interactive calculator modal
  const [tempStudentActive, setTempStudentActive] = useState(false);
  const [tempStudentQty, setTempStudentQty] = useState(2);
  const [tempPersonalActive, setTempPersonalActive] = useState(false);
  const [tempPersonalQty, setTempPersonalQty] = useState(1);
  const [tempChurchActive, setTempChurchActive] = useState(false);
  const [tempChurchQty, setTempChurchQty] = useState(15);
  const [tempDonationActive, setTempDonationActive] = useState(false);
  const [tempDonationAmount, setTempDonationAmount] = useState("");

  // Open Calculator Modal and sync state
  const handleOpenCalcModal = () => {
    setTempStudentActive(addStudentSponsorship);
    setTempStudentQty(sponsoredQty || 2);
    setTempPersonalActive(addPersonalizedCopies);
    setTempPersonalQty(personalizedQty || 1);
    setTempChurchActive(addChurchCopies);
    setTempChurchQty(churchQty || 15);
    setTempDonationActive(addAdditionalDonation);
    setTempDonationAmount(donationAmount || "");
    setShowCalcModal(true);
  };

  // Apply Modal changes back to page state
  const handleApplyCalcChanges = () => {
    setAddStudentSponsorship(tempStudentActive);
    setSponsoredQty(tempStudentQty);
    setAddPersonalizedCopies(tempPersonalActive);
    setPersonalizedQty(tempPersonalQty);
    setAddChurchCopies(tempChurchActive);
    setChurchQty(tempChurchQty);
    setAddAdditionalDonation(tempDonationActive);
    setDonationAmount(tempDonationAmount);
    setShowCalcModal(false);
    toast.success("Order calculation updated successfully!");
  };

  // Live Calculations in Modal
  const modalStudentSubtotal = tempStudentActive ? (Number(tempStudentQty) || 0) * 20 : 0;
  const modalPersonalSubtotal = tempPersonalActive ? (Number(tempPersonalQty) || 0) * 39.97 : 0;
  const modalChurchSubtotal = tempChurchActive ? (Number(tempChurchQty) || 0) * 20 : 0;
  const modalDonationSubtotal = tempDonationActive ? Number(tempDonationAmount) || 0 : 0;
  const modalTotalBooks =
    (tempStudentActive ? Number(tempStudentQty) || 0 : 0) +
    (tempPersonalActive ? Number(tempPersonalQty) || 0 : 0) +
    (tempChurchActive ? Number(tempChurchQty) || 0 : 0);
  const modalGrandTotal = modalStudentSubtotal + modalPersonalSubtotal + modalChurchSubtotal + modalDonationSubtotal;

  // Handlers for Sponsored Qty Stepper
  const handleSponsoredDecrement = () => {
    if (sponsoredQty > 2) {
      setSponsoredQty(sponsoredQty - 1);
    }
  };

  const handleSponsoredIncrement = () => {
    setSponsoredQty(sponsoredQty + 1);
  };

  const handleSponsoredQtyChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 2) {
      setSponsoredQty(val);
    } else if (e.target.value === "") {
      setSponsoredQty("");
    }
  };

  const handleSponsoredQtyBlur = () => {
    if (!sponsoredQty || sponsoredQty < 2) {
      setSponsoredQty(2);
    }
  };

  // Handlers for Personalized Qty Stepper
  const handlePersonalizedDecrement = () => {
    if (personalizedQty > 1) {
      setPersonalizedQty(personalizedQty - 1);
    }
  };

  const handlePersonalizedIncrement = () => {
    setPersonalizedQty(personalizedQty + 1);
  };

  const handlePersonalizedQtyChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 1) {
      setPersonalizedQty(val);
    } else if (e.target.value === "") {
      setPersonalizedQty("");
    }
  };

  const handlePersonalizedQtyBlur = () => {
    if (!personalizedQty || personalizedQty < 1) {
      setPersonalizedQty(1);
    }
  };

  // Handlers for Church Qty Stepper
  const handleChurchDecrement = () => {
    if (churchQty > 15) {
      setChurchQty(churchQty - 1);
    }
  };

  const handleChurchIncrement = () => {
    setChurchQty(churchQty + 1);
  };

  const handleChurchQtyChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 15) {
      setChurchQty(val);
    } else if (e.target.value === "") {
      setChurchQty("");
    }
  };

  const handleChurchQtyBlur = () => {
    if (!churchQty || churchQty < 15) {
      setChurchQty(15);
    }
  };

  // Calculations
  const sponsoredSubtotal = addStudentSponsorship ? (Number(sponsoredQty) || 0) * 20 : 0;
  const personalizedSubtotal = addPersonalizedCopies ? (Number(personalizedQty) || 0) * personalizedPrice : 0;
  const churchSubtotal = addChurchCopies ? (Number(churchQty) || 0) * churchPrice : 0;
  const donationSubtotal = (addAdditionalDonation || Number(donationAmount) > 0) && Number(donationAmount) > 0 ? Number(donationAmount) || 0 : 0;

  const totalBooksCount =
    (addStudentSponsorship ? Number(sponsoredQty) || 0 : 0) +
    (addPersonalizedCopies ? Number(personalizedQty) || 0 : 0) +
    (addChurchCopies ? Number(churchQty) || 0 : 0);

  const grandTotal = sponsoredSubtotal + personalizedSubtotal + churchSubtotal + donationSubtotal;

  // US States list for shipping address dropdown
  const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
    "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
    "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
    "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
    "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
    "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
    "Wisconsin", "Wyoming"
  ];

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    if (!email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    // Shipping address validation
    if (!shippingStreet.trim()) {
      newErrors.shippingStreet = "Street address is required";
    }
    if (!shippingCity.trim()) {
      newErrors.shippingCity = "City is required";
    }
    if (!shippingState) {
      newErrors.shippingState = "State is required";
    }
    if (!shippingZip.trim()) {
      newErrors.shippingZip = "ZIP code is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(shippingZip.trim())) {
      newErrors.shippingZip = "Please enter a valid ZIP code";
    }
    if (addPersonalizedCopies) {
      if (!recipientName.trim()) {
        newErrors.recipientName = "Recipient name is required";
      }
      if (!personalizedMessage.trim()) {
        newErrors.personalizedMessage = "Personal message is required";
      }
      if (personalizedDelivery === "different") {
        if (!personalizedDelStreet.trim()) newErrors.personalizedDelStreet = "Street address is required";
        if (!personalizedDelCity.trim()) newErrors.personalizedDelCity = "City is required";
        if (!personalizedDelState) newErrors.personalizedDelState = "State is required";
        if (!personalizedDelZip.trim()) {
          newErrors.personalizedDelZip = "ZIP code is required";
        } else if (!/^\d{5}(-\d{4})?$/.test(personalizedDelZip.trim())) {
          newErrors.personalizedDelZip = "Please enter a valid ZIP code";
        }
      }
    }
    if (addChurchCopies && churchDelivery === "different") {
      if (!churchDelStreet.trim()) newErrors.churchDelStreet = "Street address is required";
      if (!churchDelCity.trim()) newErrors.churchDelCity = "City is required";
      if (!churchDelState) newErrors.churchDelState = "State is required";
      if (!churchDelZip.trim()) {
        newErrors.churchDelZip = "ZIP code is required";
      } else if (!/^\d{5}(-\d{4})?$/.test(churchDelZip.trim())) {
        newErrors.churchDelZip = "Please enter a valid ZIP code";
      }
    }
    if (addAdditionalDonation || Number(donationAmount) > 0) {
      if (!donationAmount || isNaN(Number(donationAmount)) || Number(donationAmount) <= 0) {
        newErrors.donationAmount = "Please enter a valid donation amount";
      }
    }
    /* if (addChurchCopies) {
      if (!ministryType || !ministryType.trim()) {
        newErrors.ministryType = "Please select a ministry type";
      }
    } */

    setErrors(newErrors);

    const hasOptionSelected =
      addStudentSponsorship ||
      addPersonalizedCopies ||
      addChurchCopies ||
      ((addAdditionalDonation || Number(donationAmount) > 0) && Number(donationAmount) > 0);

    if (!hasOptionSelected) {
      toast.error("Please select at least one option to proceed.");
    } else if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields.");
    }

    return Object.keys(newErrors).length === 0 && hasOptionSelected;
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const purchaserAddress = {
      streetAddress: shippingStreet.trim(),
      apartment: shippingApt.trim(),
      city: shippingCity.trim(),
      state: shippingState,
      zipCode: shippingZip.trim(),
    };

    const payload = {
      bookTitle: "Love Smarter, Not Harder",
      ...(addStudentSponsorship && Number(sponsoredQty) > 0
        ? {
          sponseredBook: {
            quantity: Number(sponsoredQty),
            pricePerBook: 20,
            subtotal: Number(sponsoredSubtotal),
            school: currentSchool.code,
            schoolName: currentSchool.name,
            dedication: {
              organizationName: organizationName.trim() || "HBCU Hope Project",
              displayText: `Provided through the generosity of ${organizationName.trim() || "HBCU Hope Project"} for ${currentSchool.shortName} students`,
            },
          },
        }
        : {}),
      ...(addPersonalizedCopies && Number(personalizedQty) > 0
        ? {
          personalizedBook: {
            recipientName: recipientName.trim(),
            message: personalizedMessage.trim(),
            quantity: Number(personalizedQty),
            pricePerBook: 39.97,
            subtotal: Number(personalizedSubtotal),
            deliveryOption: personalizedDelivery === "same" ? "SAME_AS_SHIPPING" : "DIFFERENT_ADDRESS",
            shippingAddress: personalizedDelivery === "same"
              ? purchaserAddress
              : {
                streetAddress: personalizedDelStreet.trim(),
                apartment: personalizedDelApt.trim(),
                city: personalizedDelCity.trim(),
                state: personalizedDelState,
                zipCode: personalizedDelZip.trim(),
              },
          },
        }
        : {}),
      ...(addChurchCopies && Number(churchQty) > 0
        ? {
          churchBook: {
            quantity: Number(churchQty),
            pricePerBook: 20,
            subtotal: Number(churchSubtotal),
            forWhichMinistry: ministryType.trim(),
            ministryName: churchName.trim(),
            deliveryOption: churchDelivery === "same" ? "SAME_AS_SHIPPING" : "DIFFERENT_ADDRESS",
            shippingAddress: churchDelivery === "same"
              ? purchaserAddress
              : {
                streetAddress: churchDelStreet.trim(),
                apartment: churchDelApt.trim(),
                city: churchDelCity.trim(),
                state: churchDelState,
                zipCode: churchDelZip.trim(),
              },
          },
        }
        : {}),
      ...((addAdditionalDonation || Number(donationAmount) > 0) && Number(donationAmount) > 0
        ? {
          additionalDonation: Number(donationAmount),
        }
        : {}),
      quantity: Number(totalBooksCount),
      purchaser: {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        shippingAddress: purchaserAddress,
      },
      // Backward compatibility aliases for server service normalization
      shippingAddress: purchaserAddress,
      wallOfPromiseMessage: (addStudentSponsorship && Number(sponsoredQty) >= 3) ? wallOfPromiseMessage.trim() : "",
      requestVideoMessage: Boolean(addStudentSponsorship && Number(sponsoredQty) >= 50 && requestVideoSupport),
      requestVideoSupport: Boolean(addStudentSponsorship && Number(sponsoredQty) >= 50 && requestVideoSupport),
      receiveUpdates: Boolean(receiveUpdates),
    };

    try {
      const response = await createBookDonationSession(payload);
      if (response && response.url) {
        sessionStorage.setItem("last_donation_page", window.location.pathname);
        sessionStorage.setItem("is_book_donation", "true");
        const orderIdentifier =
          response.donationId ||
          response.orderId ||
          response.data?._id ||
          response.id;
        if (orderIdentifier) {
          sessionStorage.setItem("last_book_order_id", orderIdentifier);
        }
        window.location.href = response.url;
      } else {
        toast.error("Unable to generate checkout session. Please try again.");
      }
    } catch (err) {
      console.error("Book donation checkout error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sponsor Books. Shape Futures | Fund Life</title>
        <meta
          name="description"
          content="Sponsor Love Smarter, Not Harder books for college students and put relationship intelligence directly into their hands."
        />
      </Helmet>

      <div className="book-sponsorship-wrapper">
        {/* Top Announcement Bar */}
        <div className="top-notice-bar">
          Help place life-changing books in the hands of college students.
        </div>

        {/* Hero Section Banner */}
        <div className="hero-banner">
          <Container fluid className="px-3 px-md-5" style={{ maxWidth: "1250px" }}>
            <Row className="align-items-center g-4" style={{ marginTop: "60px" }}>
              <Col lg={7}>
                <h1 className="hero-title">
                  Sponsor Books. Shape Futures.
                </h1>
                <p className="hero-subtitle">
                  Your generosity puts relationship intelligence directly into the hands of students.
                </p>
              </Col>
              <Col lg={5} className="text-center">
                <div className="hero-book-img-wrapper">
                  <img
                    src="/girl_book2.png"
                    alt="Love Smarter, Not Harder Book"
                    className="hero-book-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* Main Section */}
        <Container className="sponsorship-container py-4 py-md-5">
          <Row className="g-4">
            {/* Left Column: Form Cards A, B, C, D, E */}
            <Col lg={7} xl={8} className="d-flex flex-column gap-4">
              {/* Section A: Sponsor Books for Students */}
              <div className="section-card">
                <div
                  className="section-badge-header"
                  onClick={() => setAddStudentSponsorship(!addStudentSponsorship)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="badge-circle">A</span>
                  <div>
                    <h3 className="section-title">Sponsor Books for Students</h3>
                    <p className="section-subtitle">
                      Choose the university and number of books you would like to sponsor.
                    </p>
                  </div>
                </div>

                <div
                  className={`impact-option-card ${addStudentSponsorship ? "selected" : ""}`}
                  onClick={() => setAddStudentSponsorship(!addStudentSponsorship)}
                >
                  <div className="impact-option-header">
                    <div className="d-flex align-items-start gap-2">
                      <span className={`custom-radio-circle ${addStudentSponsorship ? "checked" : ""}`} />
                      <span className="impact-radio-label">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <strong>Sponsor books for students</strong>
                          <span className="cover-type-tag soft-cover-tag">Soft Cover</span>
                        </div>
                        <span className="impact-subtext">
                          {currentSchool.subtext || "Provide life-changing books to help students build stronger relationships."}
                        </span>
                      </span>
                    </div>
                    <span className="impact-price-tag">
                      <strong>$20.00</strong> per book
                    </span>
                  </div>
                </div>

                {addStudentSponsorship && (
                  <div className="mt-3">
                    {/* Top Row: Left Image & Right Quantity/Perk */}
                    <div className="d-flex flex-column flex-sm-row gap-3 align-items-center mb-3">
                      {/* Left: Medium Book Image with Dynamic School Cover & Badge */}
                      <div className="sponsored-open-book-wrapper position-relative">
                        <div className="school-cover-crossfade-container">
                          {Object.entries(schoolConfig).map(([key, config]) => {
                            const isActive =
                              selectedSchool === key ||
                              (!schoolConfig[selectedSchool] && key === "hbcu");
                            return (
                              <img
                                key={key}
                                src={config.coverImage}
                                alt={config.coverAlt}
                                loading="lazy"
                                decoding="async"
                                className={`sponsored-open-book-img school-cover-switch-img ${isActive ? "active" : ""}`}
                                style={{ maxHeight: "125px", maxWidth: "190px", width: "auto", height: "auto", objectFit: "contain" }}
                              />
                            );
                          })}
                        </div>
                        <span className={`book-edition-pill ${selectedSchool === "pvamu" ? "pvamu-pill" : selectedSchool === "hbcu" ? "hbcu-pill" : "tsu-pill"}`}>
                          {currentSchool.code} EDITION
                        </span>
                      </div>

                      {/* Right: Quantity & Perk */}
                      <div className="flex-grow-1 w-100 d-flex flex-column justify-content-center">
                        <div className="qty-selector-wrapper mb-2">
                          <label className="qty-label">Number of books ({currentSchool.shortName})</label>
                          <div className="stepper-box">
                            <button
                              type="button"
                              className="stepper-btn"
                              onClick={handleSponsoredDecrement}
                            >
                              <FaMinus />
                            </button>
                            <input
                              type="text"
                              className="stepper-input"
                              value={sponsoredQty}
                              onChange={handleSponsoredQtyChange}
                              onBlur={handleSponsoredQtyBlur}
                            />
                            <button
                              type="button"
                              className="stepper-btn"
                              onClick={handleSponsoredIncrement}
                            >
                              <FaPlus />
                            </button>
                          </div>
                          <span className="tax-notice">Minimum 2 copies</span>
                        </div>

                        {/* Micro Perk Hint */}
                        {Number(sponsoredQty) < 3 ? (
                          <div className="sponsor-perk-hint mb-0">
                            <FaLightbulb className="perk-vector-icon" />
                            <span className="perk-hint-text">
                              Sponsor 3+ books to unlock a custom message on the <strong>Wall of Promise</strong>
                            </span>
                          </div>
                        ) : (
                          <div className="sponsor-perk-hint unlocked mb-0">
                            <FaCheckCircle className="perk-vector-icon perk-success-icon" />
                            <span className="perk-hint-text">
                              <strong>Wall of Promise unlocked</strong> in Section E below
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Standard Dedication with Organization / University Dropdown */}
                    <div className="dedication-box">
                      <h4 className="dedication-title">
                        Standard Dedication <span className="text-muted fw-normal small">(Included)</span>
                      </h4>
                      <p className="dedication-desc">
                        This dedication will appear on the first page of every sponsored book distributed to {selectedSchool === "hbcu" ? "HBCU" : currentSchool.name} students.
                      </p>
                      <p className="dedication-provided-by">Provided through the generosity of</p>
                      <label className="dedication-input-label">Pastor, Group or Organization Name</label>

                      <div className="custom-state-dropdown dedication-dropdown-wrapper" ref={orgDropdownRef}>
                        <div
                          className="custom-state-trigger dedication-dropdown-trigger"
                          onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                        >
                          <div className="d-flex align-items-center gap-2">
                            {selectedOrgOption === "tsu" && <span className="school-pill-dot" style={{ background: "#6F1D2F" }} />}
                            {selectedOrgOption === "pvamu" && <span className="school-pill-dot" style={{ background: "#46166b" }} />}
                            {selectedOrgOption === "hbcu" && <span className="school-pill-dot" style={{ background: "#d97706" }} />}
                            <span className="dedication-dropdown-text">
                              {selectedOrgOption === "tsu"
                                ? "I want to sponsor TSU students"
                                : selectedOrgOption === "pvamu"
                                  ? "I want to sponsor PVAMU students"
                                  : "I want to support the general HBCU population"}
                            </span>
                          </div>
                          <FaChevronDown className={`state-chevron ${orgDropdownOpen ? "open" : ""}`} />
                        </div>

                        {orgDropdownOpen && (
                          <ul className="custom-state-list dedication-dropdown-list">
                            <li
                              className={`custom-state-option ${selectedOrgOption === "tsu" ? "selected" : ""}`}
                              onClick={() => handleSelectOrg("tsu")}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <span className="d-flex align-items-center gap-2">
                                  <span className="school-pill-dot" style={{ background: "#6F1D2F" }} />
                                  <span className="dedication-dropdown-text">I want to sponsor TSU students</span>
                                </span>
                                <span className="dedication-badge-pill tsu-badge">TSU Edition</span>
                              </div>
                            </li>

                            <li
                              className={`custom-state-option ${selectedOrgOption === "pvamu" ? "selected" : ""}`}
                              onClick={() => handleSelectOrg("pvamu")}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <span className="d-flex align-items-center gap-2">
                                  <span className="school-pill-dot" style={{ background: "#46166b" }} />
                                  <span className="dedication-dropdown-text">I want to sponsor PVAMU students</span>
                                </span>
                                <span className="dedication-badge-pill pvamu-badge">PVAMU Edition</span>
                              </div>
                            </li>

                            <li
                              className={`custom-state-option ${selectedOrgOption === "hbcu" ? "selected" : ""}`}
                              onClick={() => handleSelectOrg("hbcu")}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <span className="d-flex align-items-center gap-2">
                                  <span className="school-pill-dot" style={{ background: "#d97706" }} />
                                  <span className="dedication-dropdown-text">I want to support the general HBCU community</span>
                                </span>
                                <span className="dedication-badge-pill general-badge">General HBCU</span>
                              </div>
                            </li>

                          </ul>
                        )}
                      </div>

                      <span className="dedication-subtext">
                        Select the university or program you would like to sponsor.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Section B: Purchaser Information */}
              <div className="section-card">
                <div className="section-badge-header">
                  <span className="badge-circle">B</span>
                  <div>
                    <h3 className="section-title">Purchaser Information</h3>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="form-field-label">Full name*</Form.Label>
                      <Form.Control
                        type="text"
                        value={fullName}
                        placeholder="Enter your name"
                        onChange={(e) => {
                          setFullName(e.target.value);
                          if (errors.fullName) {
                            setErrors((prev) => ({ ...prev, fullName: undefined }));
                          }
                        }}
                        isInvalid={!!errors.fullName}
                        className="custom-form-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.fullName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="form-field-label">Email address*</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        placeholder="Enter your email address"
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) {
                            setErrors((prev) => ({ ...prev, email: undefined }));
                          }
                        }}
                        isInvalid={!!errors.email}
                        className="custom-form-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="form-field-label">
                        Phone number <span className="optional-tag-small">(Optional)</span>
                      </Form.Label>
                      <PhoneInput
                        country={"us"}
                        enableSearch={true}
                        value={phone}
                        onChange={(phoneVal) => {
                          setPhone(phoneVal);
                          if (errors.phone) {
                            setErrors((prev) => ({ ...prev, phone: undefined }));
                          }
                        }}
                        inputClass={`custom-form-input w-100 ${errors.phone ? "is-invalid border-danger" : ""}`}
                        containerClass="book-phone-input-container"
                        buttonClass="book-phone-button"
                      />
                      {errors.phone && (
                        <div className="invalid-feedback d-block mt-1" style={{ fontSize: "0.75rem" }}>
                          {errors.phone}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Shipping Address */}
                <div className="shipping-address-section">
                  <div className="shipping-address-header">
                    <FaTruck className="shipping-icon" />
                    <span className="shipping-title">Shipping Address</span>
                  </div>
                  <hr className="shipping-divider" />
                  <Row className="g-3">
                    <Col md={7}>
                      <Form.Group>
                        <Form.Label className="form-field-label">Street address*</Form.Label>
                        <Form.Control
                          type="text"
                          value={shippingStreet}
                          placeholder="Enter your street address"
                          onChange={(e) => {
                            setShippingStreet(e.target.value);
                            if (errors.shippingStreet) {
                              setErrors((prev) => ({ ...prev, shippingStreet: undefined }));
                            }
                          }}
                          isInvalid={!!errors.shippingStreet}
                          className="custom-form-input"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingStreet}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label className="form-field-label">
                          Apartment, suite, unit, etc. <span className="optional-tag-small">(Optional)</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={shippingApt}
                          placeholder="Enter your apartment, suite, unit, etc."
                          onChange={(e) => setShippingApt(e.target.value)}
                          className="custom-form-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-field-label">City*</Form.Label>
                        <Form.Control
                          type="text"
                          value={shippingCity}
                          placeholder="Enter your city"
                          onChange={(e) => {
                            setShippingCity(e.target.value);
                            if (errors.shippingCity) {
                              setErrors((prev) => ({ ...prev, shippingCity: undefined }));
                            }
                          }}
                          isInvalid={!!errors.shippingCity}
                          className="custom-form-input"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingCity}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-field-label">State*</Form.Label>
                        <div className="custom-state-dropdown" ref={stateDropdownRef}>
                          <div
                            className={`custom-state-trigger custom-form-input ${errors.shippingState ? "is-invalid border-danger" : ""}`}
                            onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
                          >
                            <span className={shippingState ? "" : "placeholder-text"}>
                              {shippingState || "Select state"}
                            </span>
                            <FaChevronDown className={`state-chevron ${stateDropdownOpen ? "open" : ""}`} />
                          </div>
                          {stateDropdownOpen && (
                            <ul className="custom-state-list">
                              {usStates.map((state) => (
                                <li
                                  key={state}
                                  className={`custom-state-option ${shippingState === state ? "selected" : ""}`}
                                  onClick={() => {
                                    setShippingState(state);
                                    setStateDropdownOpen(false);
                                    if (errors.shippingState) {
                                      setErrors((prev) => ({ ...prev, shippingState: undefined }));
                                    }
                                  }}
                                >
                                  {state}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {errors.shippingState && (
                          <div className="invalid-feedback d-block mt-1" style={{ fontSize: "0.75rem" }}>
                            {errors.shippingState}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-field-label">ZIP Code*</Form.Label>
                        <Form.Control
                          type="text"
                          value={shippingZip}
                          placeholder="10001"
                          onChange={(e) => {
                            setShippingZip(e.target.value);
                            if (errors.shippingZip) {
                              setErrors((prev) => ({ ...prev, shippingZip: undefined }));
                            }
                          }}
                          isInvalid={!!errors.shippingZip}
                          className="custom-form-input"
                          maxLength={10}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingZip}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* Section C: Add Copies for Your Church (Optional) */}
              <div className="section-card">
                <div className="section-badge-header justify-content-between align-items-start">
                  <div
                    className="d-flex align-items-start gap-3"
                    onClick={() => setAddChurchCopies(!addChurchCopies)}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="badge-circle">C</span>
                    <div>
                      <h3 className="section-title">
                        Add copies for your church, business, or organization <span className="optional-tag">(Optional)</span>
                      </h3>
                      <p className="section-subtitle">
                        Ideal for youth, college, singles, and young adult ministries.
                      </p>
                    </div>
                  </div>
                  <div className="church-price-badge">
                    <span className="price-main">$20 each</span>
                  </div>
                </div>

                <div
                  className={`impact-option-card ${addChurchCopies ? "selected" : ""}`}
                  onClick={() => setAddChurchCopies(!addChurchCopies)}
                >
                  <div className="impact-option-header">
                    <div className="d-flex align-items-start gap-2">
                      <span className={`custom-radio-circle ${addChurchCopies ? "checked" : ""}`} />
                      <span className="impact-radio-label">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <strong>Add copies for your church, business, or organization</strong>
                          <span className="cover-type-tag soft-cover-tag">Soft Cover</span>
                        </div>
                        <span className="impact-subtext">
                          Share these books within your church, business, or organization.
                        </span>
                      </span>
                    </div>
                    <span className="impact-price-tag">
                      <strong>$20.00</strong> per book
                    </span>
                  </div>
                </div>

                {addChurchCopies && (
                  <div className="mt-3">
                    {/* Top Row: Left Image & Right Quantity/Note */}
                    <div className="d-flex flex-column flex-sm-row gap-3 align-items-center mb-3">
                      {/* Left: Medium Book Image */}
                      <div className="sponsored-open-book-wrapper">
                        <img
                          src="/soft_cover.png"
                          alt="Founder's Edition Softcover"
                          className="sponsored-open-book-img"
                          loading="lazy"
                          decoding="async"
                          style={{ maxHeight: "125px", maxWidth: "190px", width: "auto", height: "auto", objectFit: "contain" }}
                        />
                      </div>

                      {/* Right: Quantity & Note */}
                      <div className="flex-grow-1 w-100 d-flex flex-column justify-content-center">
                        <div className="qty-selector-wrapper mb-2">
                          <label className="qty-label">Number of church copies</label>
                          <div className="stepper-box">
                            <button
                              type="button"
                              className="stepper-btn"
                              onClick={handleChurchDecrement}
                            >
                              <FaMinus />
                            </button>
                            <input
                              type="text"
                              className="stepper-input"
                              value={churchQty}
                              onChange={handleChurchQtyChange}
                              onBlur={handleChurchQtyBlur}
                            />
                            <button
                              type="button"
                              className="stepper-btn"
                              onClick={handleChurchIncrement}
                            >
                              <FaPlus />
                            </button>
                          </div>
                          <span className="tax-notice">Minimum 15 copies</span>
                        </div>

                        <div className="yellow-info-alert mt-1 mb-0">
                          <FaInfoCircle className="alert-icon flex-shrink-0" />
                          <span>
                            <strong>Note:</strong> Shipping and Handling is included.
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Address */}
                    <div className="delivery-address-box mt-3">
                      <strong className="delivery-address-label">Delivery Address :</strong>
                      <div className="delivery-radio-row">
                        <label className="delivery-radio-option">
                          <input
                            type="radio"
                            name="churchDelivery"
                            value="same"
                            checked={churchDelivery === "same"}
                            onChange={() => {
                              setChurchDelivery("same");
                              setErrors((prev) => ({
                                ...prev,
                                churchDelStreet: undefined,
                                churchDelCity: undefined,
                                churchDelState: undefined,
                                churchDelZip: undefined,
                              }));
                            }}
                          />
                          <span>Use my shipping address (above)</span>
                        </label>
                        <label className="delivery-radio-option">
                          <input
                            type="radio"
                            name="churchDelivery"
                            value="different"
                            checked={churchDelivery === "different"}
                            onChange={() => setChurchDelivery("different")}
                          />
                          <span>Ship to a different address</span>
                        </label>
                      </div>
                      {churchDelivery === "different" && (
                        <Row className="g-2 mt-2">
                          <Col md={7}>
                            <Form.Control
                              type="text"
                              value={churchDelStreet}
                              placeholder="Street address *"
                              onChange={(e) => {
                                setChurchDelStreet(e.target.value);
                                if (errors.churchDelStreet) setErrors((prev) => ({ ...prev, churchDelStreet: undefined }));
                              }}
                              isInvalid={!!errors.churchDelStreet}
                              className="custom-form-input"
                            />
                            <Form.Control.Feedback type="invalid">{errors.churchDelStreet}</Form.Control.Feedback>
                          </Col>
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              value={churchDelApt}
                              placeholder="Apt, suite, unit (Optional)"
                              onChange={(e) => setChurchDelApt(e.target.value)}
                              className="custom-form-input"
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Control
                              type="text"
                              value={churchDelCity}
                              placeholder="City *"
                              onChange={(e) => {
                                setChurchDelCity(e.target.value);
                                if (errors.churchDelCity) setErrors((prev) => ({ ...prev, churchDelCity: undefined }));
                              }}
                              isInvalid={!!errors.churchDelCity}
                              className="custom-form-input"
                            />
                            <Form.Control.Feedback type="invalid">{errors.churchDelCity}</Form.Control.Feedback>
                          </Col>
                          <Col md={4}>
                            <div className="custom-state-dropdown" ref={churchStateDropdownRef}>
                              <div
                                className={`custom-state-trigger custom-form-input ${errors.churchDelState ? "is-invalid border-danger" : ""}`}
                                onClick={() => setChurchStateDropdownOpen(!churchStateDropdownOpen)}
                              >
                                <span className={churchDelState ? "" : "placeholder-text"}>
                                  {churchDelState || "State *"}
                                </span>
                                <FaChevronDown className={`state-chevron ${churchStateDropdownOpen ? "open" : ""}`} />
                              </div>
                              {churchStateDropdownOpen && (
                                <ul className="custom-state-list">
                                  {usStates.map((state) => (
                                    <li
                                      key={state}
                                      className={`custom-state-option ${churchDelState === state ? "selected" : ""}`}
                                      onClick={() => {
                                        setChurchDelState(state);
                                        setChurchStateDropdownOpen(false);
                                        if (errors.churchDelState) {
                                          setErrors((prev) => ({ ...prev, churchDelState: undefined }));
                                        }
                                      }}
                                    >
                                      {state}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            {errors.churchDelState && (
                              <div className="invalid-feedback d-block">{errors.churchDelState}</div>
                            )}
                          </Col>
                          <Col md={4}>
                            <Form.Control
                              type="text"
                              value={churchDelZip}
                              placeholder="ZIP Code *"
                              onChange={(e) => {
                                setChurchDelZip(e.target.value);
                                if (errors.churchDelZip) setErrors((prev) => ({ ...prev, churchDelZip: undefined }));
                              }}
                              isInvalid={!!errors.churchDelZip}
                              className="custom-form-input"
                              maxLength={10}
                            />
                            <Form.Control.Feedback type="invalid">{errors.churchDelZip}</Form.Control.Feedback>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Original Cover Banner Box */}
              <div className="original-cover-banner-card">
                <div className="banner-left-col">
                  <img
                    src="/newBook2.png"
                    alt="Personalized Founder's Edition Hardcover"
                    className="banner-book-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="banner-middle-col">
                  <h3 className="banner-main-title">Personalized Founder's Edition</h3>
                  <h4 className="banner-sub-title">Hardcover</h4>
                  <p className="banner-desc">
                    A premium edition for that special young person in your life.
                  </p>
                </div>
              </div>

              {/* Section D: Add Personalized Copies (Optional) */}
              <div className="section-card">
                <div
                  className="section-badge-header"
                  onClick={() => setAddPersonalizedCopies(!addPersonalizedCopies)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="badge-circle">D</span>
                  <div>
                    <h3 className="section-title">
                      Add Personalized Copies <span className="optional-tag">(Optional)</span>
                    </h3>
                    <p className="section-subtitle">
                      Order personal copies with a custom message for the recipient.
                    </p>
                  </div>
                </div>

                <div
                  className={`impact-option-card ${addPersonalizedCopies ? "selected" : ""}`}
                  onClick={() => setAddPersonalizedCopies(!addPersonalizedCopies)}
                >
                  <div className="impact-option-header">
                    <div className="d-flex align-items-start gap-2">
                      <span className={`custom-radio-circle ${addPersonalizedCopies ? "checked" : ""}`} />
                      <span className="impact-radio-label">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <strong>Add personalized copies</strong>
                          <span className="cover-type-tag hard-cover-tag">Hard Cover</span>
                        </div>
                      </span>
                    </div>
                    <span className="impact-price-tag">
                      <strong>$39.97</strong> each
                    </span>
                  </div>
                </div>

                {addPersonalizedCopies && (
                  <div className="mt-3">
                    {/* Top Row: Left Image & Right Quantity/Note */}
                    <div className="d-flex flex-column flex-sm-row gap-3 align-items-center mb-3">
                      {/* Left: Medium Book Image */}
                      <div className="sponsored-open-book-wrapper">
                        <img
                          src="/newBook2.png"
                          alt="Personalized Founder's Edition Hardcover"
                          className="sponsored-open-book-img"
                          loading="lazy"
                          decoding="async"
                          style={{ maxHeight: "125px", maxWidth: "190px", width: "auto", height: "auto", objectFit: "contain" }}
                        />
                      </div>

                      {/* Right: Quantity & Note */}
                      <div className="flex-grow-1 w-100 d-flex flex-column justify-content-center">
                        <div className="qty-selector-wrapper mb-2">
                          <label className="qty-label d-block">Number of copies</label>
                          <div className="stepper-box">
                            <button
                              type="button"
                              className="stepper-btn"
                              onClick={handlePersonalizedDecrement}
                            >
                              <FaMinus />
                            </button>
                            <input
                              type="text"
                              className="stepper-input"
                              value={personalizedQty}
                              onChange={handlePersonalizedQtyChange}
                              onBlur={handlePersonalizedQtyBlur}
                            />
                            <button
                              type="button"
                              className="stepper-btn"
                              onClick={handlePersonalizedIncrement}
                            >
                              <FaPlus />
                            </button>
                          </div>
                        </div>

                        <div className="yellow-info-alert mt-1 mb-0">
                          <FaInfoCircle className="alert-icon flex-shrink-0" />
                          <span>
                            The same recipient name and message will be printed in all personal copies in this order.
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="personalized-box">
                      <Form.Group className="mb-3">
                        <Form.Label className="form-field-label">Recipient name*</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Jordan"
                          value={recipientName}
                          onChange={(e) => {
                            setRecipientName(e.target.value);
                            if (errors.recipientName) {
                              setErrors((prev) => ({ ...prev, recipientName: undefined }));
                            }
                          }}
                          isInvalid={!!errors.recipientName}
                          className="custom-form-input"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.recipientName}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-0">
                        <Form.Label className="form-field-label">Personal message to recipient*</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          maxLength={250}
                          placeholder="Write your personal message..."
                          value={personalizedMessage}
                          onChange={(e) => {
                            setPersonalizedMessage(e.target.value);
                            if (errors.personalizedMessage) {
                              setErrors((prev) => ({ ...prev, personalizedMessage: undefined }));
                            }
                          }}
                          isInvalid={!!errors.personalizedMessage}
                          className="custom-form-input"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.personalizedMessage}
                        </Form.Control.Feedback>
                        <div className="char-count-text">
                          {personalizedMessage.length}/250 characters
                        </div>
                      </Form.Group>
                    </div>

                    {/* Delivery Address */}
                    <div className="delivery-address-box mt-3">
                      <strong className="delivery-address-label">Delivery Address :</strong>
                      <div className="delivery-radio-row">
                        <label className="delivery-radio-option">
                          <input
                            type="radio"
                            name="personalizedDelivery"
                            value="same"
                            checked={personalizedDelivery === "same"}
                            onChange={() => {
                              setPersonalizedDelivery("same");
                              setErrors((prev) => ({
                                ...prev,
                                personalizedDelStreet: undefined,
                                personalizedDelCity: undefined,
                                personalizedDelState: undefined,
                                personalizedDelZip: undefined,
                              }));
                            }}
                          />
                          <span>Use my shipping address (above)</span>
                        </label>
                        <label className="delivery-radio-option">
                          <input
                            type="radio"
                            name="personalizedDelivery"
                            value="different"
                            checked={personalizedDelivery === "different"}
                            onChange={() => setPersonalizedDelivery("different")}
                          />
                          <span>Ship to a different address</span>
                        </label>
                      </div>
                      {personalizedDelivery === "different" && (
                        <Row className="g-2 mt-2">
                          <Col md={7}>
                            <Form.Control
                              type="text"
                              value={personalizedDelStreet}
                              placeholder="Street address *"
                              onChange={(e) => {
                                setPersonalizedDelStreet(e.target.value);
                                if (errors.personalizedDelStreet) setErrors((prev) => ({ ...prev, personalizedDelStreet: undefined }));
                              }}
                              isInvalid={!!errors.personalizedDelStreet}
                              className="custom-form-input"
                            />
                            <Form.Control.Feedback type="invalid">{errors.personalizedDelStreet}</Form.Control.Feedback>
                          </Col>
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              value={personalizedDelApt}
                              placeholder="Apt, suite, unit (Optional)"
                              onChange={(e) => setPersonalizedDelApt(e.target.value)}
                              className="custom-form-input"
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Control
                              type="text"
                              value={personalizedDelCity}
                              placeholder="City *"
                              onChange={(e) => {
                                setPersonalizedDelCity(e.target.value);
                                if (errors.personalizedDelCity) setErrors((prev) => ({ ...prev, personalizedDelCity: undefined }));
                              }}
                              isInvalid={!!errors.personalizedDelCity}
                              className="custom-form-input"
                            />
                            <Form.Control.Feedback type="invalid">{errors.personalizedDelCity}</Form.Control.Feedback>
                          </Col>
                          <Col md={4}>
                            <div className="custom-state-dropdown" ref={personalizedStateDropdownRef}>
                              <div
                                className={`custom-state-trigger custom-form-input ${errors.personalizedDelState ? "is-invalid border-danger" : ""}`}
                                onClick={() => setPersonalizedStateDropdownOpen(!personalizedStateDropdownOpen)}
                              >
                                <span className={personalizedDelState ? "" : "placeholder-text"}>
                                  {personalizedDelState || "State *"}
                                </span>
                                <FaChevronDown className={`state-chevron ${personalizedStateDropdownOpen ? "open" : ""}`} />
                              </div>
                              {personalizedStateDropdownOpen && (
                                <ul className="custom-state-list">
                                  {usStates.map((state) => (
                                    <li
                                      key={state}
                                      className={`custom-state-option ${personalizedDelState === state ? "selected" : ""}`}
                                      onClick={() => {
                                        setPersonalizedDelState(state);
                                        setPersonalizedStateDropdownOpen(false);
                                        if (errors.personalizedDelState) {
                                          setErrors((prev) => ({ ...prev, personalizedDelState: undefined }));
                                        }
                                      }}
                                    >
                                      {state}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            {errors.personalizedDelState && (
                              <div className="invalid-feedback d-block">{errors.personalizedDelState}</div>
                            )}
                          </Col>
                          <Col md={4}>
                            <Form.Control
                              type="text"
                              value={personalizedDelZip}
                              placeholder="ZIP Code *"
                              onChange={(e) => {
                                setPersonalizedDelZip(e.target.value);
                                if (errors.personalizedDelZip) setErrors((prev) => ({ ...prev, personalizedDelZip: undefined }));
                              }}
                              isInvalid={!!errors.personalizedDelZip}
                              className="custom-form-input"
                              maxLength={10}
                            />
                            <Form.Control.Feedback type="invalid">{errors.personalizedDelZip}</Form.Control.Feedback>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Section E: Wall of Promise Message (Dynamic: appears for sponsors of 3+ copies) */}
              {addStudentSponsorship && Number(sponsoredQty) >= 3 && (
                <div className="wall-of-promise-card-container">
                  <div className="section-badge-header align-items-start mb-2">
                    <span className="badge-circle">E</span>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <h3 className="section-title text-dark-red mb-0">
                          Wall of Promise
                        </h3>
                        <span className="unlocked-badge">
                          Unlocked!
                        </span>
                      </div>
                      <p className="section-subtitle mt-1">
                        Sponsors of 3+ copies can leave a message of hope for the young adults that will receive their books.
                      </p>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Form.Group className="mb-0">
                      <Form.Label className="wall-input-heading">
                        Write a short message of hope &amp; encouragement
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={250}
                        placeholder="Write your encouraging message here..."
                        value={wallOfPromiseMessage}
                        onChange={(e) => setWallOfPromiseMessage(e.target.value)}
                        className="wall-custom-textarea"
                      />
                      <div className="char-count-text text-end mt-1">
                        {wallOfPromiseMessage.length}/250 characters
                      </div>
                    </Form.Group>

                    {/* Sponsored 50+ copies video message box */}
                    <div className="video-perk-box mt-3">
                      <div className="d-flex align-items-center justify-content-between gap-3">
                        <div className="flex-grow-1">
                          <h4 className="video-perk-title">Sponsored 50+ copies?</h4>
                          <p className="video-perk-subtext">
                            You can request that a HBCU Hope Project team member reach out to you for a video message of support.
                          </p>
                          <div className="video-perk-check-wrapper mt-2">
                            <Form.Check
                              type="checkbox"
                              id="request-video-support"
                              className="video-perk-checkbox d-flex align-items-start gap-2"
                              checked={requestVideoSupport}
                              onChange={(e) => setRequestVideoSupport(e.target.checked)}
                              label={
                                <div>
                                  <span className="video-perk-check-label">
                                    Yes, please reach out to me for a video message of support.
                                  </span>
                                  <span className="video-perk-contact-note">
                                    We will contact you using the information provided above.
                                  </span>
                                </div>
                              }
                            />
                          </div>
                        </div>

                        <div className="video-camera-icon-badge flex-shrink-0">
                          <svg
                            width="34"
                            height="34"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#9b1c1c"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="2" y="5" width="13" height="14" rx="2.5" />
                            <path d="M15 10l5-3.5v11l-5-3.5v-4z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Section F: Add an Additional Donation (Optional) */}
              <div className="section-card">
                <div
                  className="section-badge-header"
                  onClick={() => setAddAdditionalDonation(!addAdditionalDonation)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="badge-circle">F</span>
                  <div>
                    <h3 className="section-title">
                      Add an Additional Donation <span className="optional-tag">(Optional)</span>
                    </h3>
                    <p className="section-subtitle">
                      Support the HBCU community with an extra charitable gift of any amount.
                    </p>
                  </div>
                </div>

                <div
                  className={`impact-option-card ${addAdditionalDonation ? "selected" : ""}`}
                  onClick={() => setAddAdditionalDonation(!addAdditionalDonation)}
                >
                  <div className="impact-option-header">
                    <div className="d-flex align-items-start gap-2">
                      <span className={`custom-radio-circle ${addAdditionalDonation ? "checked" : ""}`} />
                      <span className="impact-radio-label">
                        <strong>Add a donation amount</strong>
                        <span className="impact-subtext">
                          Provide additional financial support to expand student outreach.
                        </span>
                      </span>
                    </div>
                    <span className="impact-price-tag">
                      <strong>Any amount</strong>
                    </span>
                  </div>
                </div>

                {addAdditionalDonation && (
                  <div className="donation-box mt-3">
                    <Form.Group className="mb-2">
                      <Form.Label className="form-field-label">Donation Amount ($USD)</Form.Label>
                      <div className="donation-input-wrapper">
                        <span className="donation-currency-symbol">$</span>
                        <Form.Control
                          type="number"
                          min="1"
                          step="any"
                          placeholder="Enter amount (e.g., 25)"
                          value={donationAmount}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDonationAmount(val);
                            if (Number(val) > 0) setAddAdditionalDonation(true);
                            if (errors.donationAmount) {
                              setErrors((prev) => ({ ...prev, donationAmount: undefined }));
                            }
                          }}
                          isInvalid={!!errors.donationAmount}
                          className="custom-form-input donation-input"
                        />
                      </div>
                      {errors.donationAmount && (
                        <div className="invalid-feedback d-block mt-1" style={{ fontSize: "0.75rem" }}>
                          {errors.donationAmount}
                        </div>
                      )}
                    </Form.Group>

                    <div className="donation-presets d-flex gap-2 flex-wrap mt-2">
                      {[10, 25, 50, 100, 250].map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          className={`donation-preset-pill ${Number(donationAmount) === amt ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDonationAmount(amt);
                            setAddAdditionalDonation(true);
                            if (errors.donationAmount) {
                              setErrors((prev) => ({ ...prev, donationAmount: undefined }));
                            }
                          }}
                        >
                          +${amt}
                        </button>
                      ))}
                    </div>

                    <div className="yellow-info-alert mt-3">
                      <FaInfoCircle className="alert-icon flex-shrink-0" />
                      <span>
                        Your generous gift fuels a brighter future — directly empowering students with life-changing relationship wisdom, mentorship, and hope.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </Col>

            {/* Right Column: Order Summary Sidebar */}
            <Col lg={5} xl={4}>
              <div className="summary-sticky-card">
                <h3 className="summary-title">Order Summary</h3>

                <div className="summary-item-header">
                  <div className="summary-book-info">
                    <h4 className="summary-book-name">Love Smarter, Not Harder</h4>
                    <span className="summary-author-name">By Mike Goss</span>
                  </div>
                </div>

                <div className="summary-breakdown">
                  {addStudentSponsorship && sponsoredQty > 0 && (
                    <div className="summary-row">
                      <span>
                        {sponsoredQty} sponsor books ({currentSchool.code} Edition) * $20.00
                      </span>
                      <span>${sponsoredSubtotal.toFixed(2)}</span>
                    </div>
                  )}

                  {addChurchCopies && churchQty > 0 && (
                    <div className="summary-row">
                      <span>
                        {churchQty} church copies * ${churchPrice.toFixed(2)}
                      </span>
                      <span>${churchSubtotal.toFixed(2)}</span>
                    </div>
                  )}

                  {addPersonalizedCopies && personalizedQty > 0 && (
                    <div className="summary-row">
                      <span>
                        {personalizedQty} personalized copies * ${personalizedPrice.toFixed(2)}
                      </span>
                      <span>${personalizedSubtotal.toFixed(2)}</span>
                    </div>
                  )}

                  {addAdditionalDonation && Number(donationAmount) > 0 && (
                    <div className="summary-row">
                      <span>Additional Donation</span>
                      <span>${Number(donationAmount).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="summary-row">
                    <span className="text-muted">Sales tax</span>
                    <span className="text-muted">Calculated at checkout</span>
                  </div>
                </div>

                <hr className="summary-divider" />

                <div className="summary-total-row">
                  <span className="total-label">Total</span>
                  <div className="total-amount-box">
                    <span className="total-amount">${grandTotal.toFixed(2)}</span>
                    <div
                      className="calc-badge"
                      onClick={handleOpenCalcModal}
                      title="Click to open interactive calculator"
                    >
                      <FaCalculator className="calc-icon-white" />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="continue-payment-btn mt-3"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    "Continue to Secure Payment"
                  )}
                </button>

                <div className="summary-security-text mt-2">
                  <FaLock className="me-1 security-lock-icon" /> Secure payment powered by Stripe
                </div>

                <div className="payment-delivery-info mt-2">
                  <FaTruck className="delivery-info-icon" />
                  <span>
                    Please allow up to <strong>21 days</strong> to receive your order.
                  </span>
                </div>

                <div className="receive-updates-card">
                  <Form.Check
                    type="checkbox"
                    id="receive-updates"
                    className="receive-updates-check m-0 d-flex align-items-start gap-2"
                    checked={receiveUpdates}
                    onChange={(e) => setReceiveUpdates(e.target.checked)}
                    label={
                      <span className="receive-updates-text">
                        Check here to receive updates as we hit important milestones. (We'll only send updates related to your sponsorship order.)
                      </span>
                    }
                  />
                </div>

                <div className="summary-help-footer mt-2">
                  <div className="heart-circle-icon">
                    <FaHeart />
                  </div>
                  <span>
                    Your sponsorship helps equip students for success in relationships, college, and life.
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Interactive Order & Price Calculator Modal */}
      <Modal show={showCalcModal} onHide={() => setShowCalcModal(false)} centered dialogClassName="compact-calc-modal">
        <Modal.Header closeButton className="calc-modal-header border-0">
          <div className="d-flex align-items-center gap-2">
            <div className="calc-header-icon-badge">
              <FaCalculator className="modal-header-calc-icon" />
            </div>
            <div>
              <Modal.Title style={{ fontSize: "1.05rem", fontWeight: "700" }} className="text-white mb-0">
                Interactive Order Calculator
              </Modal.Title>
              <div className="text-white-50" style={{ fontSize: "0.72rem" }}>
                Adjust quantities below to simulate or update your order
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-3" style={{ backgroundColor: "#faf8f5" }}>
          <div className="d-flex flex-column gap-2 mb-3">
            {/* Student Sponsored Books */}
            <div className={`calc-item-row p-2 px-3 bg-white rounded-3 shadow-sm ${tempStudentActive ? "active" : ""}`}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <Form.Check
                  type="checkbox"
                  id="modal-student-check"
                  checked={tempStudentActive}
                  onChange={(e) => setTempStudentActive(e.target.checked)}
                  label={
                    <div>
                      <strong style={{ fontSize: "0.88rem", color: "#222" }}>Student Sponsored Books</strong>
                      <span className="cover-type-tag soft-cover-tag ms-2">Soft Cover</span>
                      <div className="text-muted" style={{ fontSize: "0.74rem" }}>$20.00 per book (Min 2)</div>
                    </div>
                  }
                />
                {tempStudentActive && (
                  <div className="d-flex align-items-center gap-2">
                    <div className="stepper-box">
                      <button
                        type="button"
                        className="stepper-btn"
                        style={{ width: "30px", height: "30px" }}
                        onClick={() => setTempStudentQty(Math.max(2, tempStudentQty - 1))}
                      >
                        <FaMinus />
                      </button>
                      <input
                        type="text"
                        className="stepper-input"
                        style={{ width: "42px", height: "30px", fontSize: "0.88rem" }}
                        value={tempStudentQty}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 2) setTempStudentQty(val);
                        }}
                        onBlur={() => {
                          if (!tempStudentQty || tempStudentQty < 2) setTempStudentQty(2);
                        }}
                      />
                      <button
                        type="button"
                        className="stepper-btn"
                        style={{ width: "30px", height: "30px" }}
                        onClick={() => setTempStudentQty(tempStudentQty + 1)}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <span className="fw-bold" style={{ minWidth: "70px", textAlign: "right", color: "#840b0b" }}>
                      ${modalStudentSubtotal.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Church Copies */}
            <div className={`calc-item-row p-2 px-3 bg-white rounded-3 shadow-sm ${tempChurchActive ? "active" : ""}`}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <Form.Check
                  type="checkbox"
                  id="modal-church-check"
                  checked={tempChurchActive}
                  onChange={(e) => setTempChurchActive(e.target.checked)}
                  label={
                    <div>
                      <strong style={{ fontSize: "0.88rem", color: "#222" }}>Church, Business, or Organization</strong>
                      <span className="cover-type-tag soft-cover-tag ms-2">Soft Cover</span>
                      <div className="text-muted" style={{ fontSize: "0.74rem" }}>$20.00 per book (Min 15)</div>
                    </div>
                  }
                />
                {tempChurchActive && (
                  <div className="d-flex align-items-center gap-2">
                    <div className="stepper-box">
                      <button
                        type="button"
                        className="stepper-btn"
                        style={{ width: "30px", height: "30px" }}
                        onClick={() => setTempChurchQty(Math.max(15, tempChurchQty - 1))}
                      >
                        <FaMinus />
                      </button>
                      <input
                        type="text"
                        className="stepper-input"
                        style={{ width: "42px", height: "30px", fontSize: "0.88rem" }}
                        value={tempChurchQty}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 15) setTempChurchQty(val);
                        }}
                        onBlur={() => {
                          if (!tempChurchQty || tempChurchQty < 15) setTempChurchQty(15);
                        }}
                      />
                      <button
                        type="button"
                        className="stepper-btn"
                        style={{ width: "30px", height: "30px" }}
                        onClick={() => setTempChurchQty(tempChurchQty + 1)}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <span className="fw-bold" style={{ minWidth: "70px", textAlign: "right", color: "#840b0b" }}>
                      ${modalChurchSubtotal.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Personalized Copies */}
            <div className={`calc-item-row p-2 px-3 bg-white rounded-3 shadow-sm ${tempPersonalActive ? "active" : ""}`}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <Form.Check
                  type="checkbox"
                  id="modal-personal-check"
                  checked={tempPersonalActive}
                  onChange={(e) => setTempPersonalActive(e.target.checked)}
                  label={
                    <div>
                      <strong style={{ fontSize: "0.88rem", color: "#222" }}>Personalized Copies</strong>
                      <span className="cover-type-tag hard-cover-tag ms-2">Hard Cover</span>
                      <div className="text-muted" style={{ fontSize: "0.74rem" }}>$39.97 each</div>
                    </div>
                  }
                />
                {tempPersonalActive && (
                  <div className="d-flex align-items-center gap-2">
                    <div className="stepper-box">
                      <button
                        type="button"
                        className="stepper-btn"
                        style={{ width: "30px", height: "30px" }}
                        onClick={() => setTempPersonalQty(Math.max(1, tempPersonalQty - 1))}
                      >
                        <FaMinus />
                      </button>
                      <input
                        type="text"
                        className="stepper-input"
                        style={{ width: "42px", height: "30px", fontSize: "0.88rem" }}
                        value={tempPersonalQty}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 1) {
                            setTempPersonalQty(val);
                          } else if (e.target.value === "") {
                            setTempPersonalQty("");
                          }
                        }}
                        onBlur={() => {
                          if (!tempPersonalQty || tempPersonalQty < 1) {
                            setTempPersonalQty(1);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="stepper-btn"
                        style={{ width: "30px", height: "30px" }}
                        onClick={() => setTempPersonalQty(tempPersonalQty + 1)}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <span className="fw-bold" style={{ minWidth: "70px", textAlign: "right", color: "#840b0b" }}>
                      ${modalPersonalSubtotal.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Donation */}
            <div className={`calc-item-row p-2 px-3 bg-white rounded-3 shadow-sm ${tempDonationActive ? "active" : ""}`}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <Form.Check
                  type="checkbox"
                  id="modal-donation-check"
                  checked={tempDonationActive}
                  onChange={(e) => setTempDonationActive(e.target.checked)}
                  label={
                    <div>
                      <strong style={{ fontSize: "0.88rem", color: "#222" }}>Additional Donation</strong>
                      <div className="text-muted" style={{ fontSize: "0.74rem" }}>Any custom gift amount</div>
                    </div>
                  }
                />
                {tempDonationActive && (
                  <div className="d-flex align-items-center gap-2">
                    <div className="donation-modal-input-wrap">
                      <span className="donation-modal-prefix">$</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Amount"
                        className="stepper-input donation-modal-input"
                        value={tempDonationAmount}
                        onChange={(e) => setTempDonationAmount(e.target.value)}
                      />
                    </div>
                    <span className="fw-bold" style={{ minWidth: "70px", textAlign: "right", color: "#840b0b" }}>
                      ${(Number(tempDonationAmount) || 0).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Premium Burgundy & Gold Summary Banner */}
          <div className="calc-summary-banner p-3 rounded-3 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "0.78rem" }}>
              <span className="text-white">Total Books Selected:</span>
              <span className="badge-books-count">
                {modalTotalBooks} books
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "0.78rem" }}>
              <span className="text-white">Estimated Sales Tax:</span>
              <span className="text-white">Calculated at checkout</span>
            </div>
            <div className="calc-banner-divider my-2"></div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-bold text-white" style={{ fontSize: "0.95rem" }}>Calculated Total:</span>
              <span className="calc-banner-total">${modalGrandTotal.toFixed(2)}</span>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="justify-content-between py-2 px-3 border-0 bg-white">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
            onClick={() => setShowCalcModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm px-4 rounded-pill shadow-sm"
            style={{ background: "#f7b500", color: "#111111", fontWeight: "700", border: "none" }}
            onClick={handleApplyCalcChanges}
          >
            Apply & Update Order (${modalGrandTotal.toFixed(2)})
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BookSponsorshipPage;
